import React from "react";
import {
  AbsoluteFill,
  interpolate,
  spring,
  useCurrentFrame,
  useVideoConfig,
} from "remotion";

/** 默认中文字体栈（思源黑体 / 系统黑体） */
const defaultFontFamily = `"PingFang SC", "Microsoft YaHei", "Noto Sans CJK SC", "Source Han Sans SC", sans-serif`;

interface SmashTextProps {
  mainText: string;
  subText?: string;
  style: {
    theme: string;
    entrance: "drop" | "glitch" | "splitReveal" | "fadeIn";
    textStroke?: string;
    highlight?: string[];
    highlightColor?: string;
    /** 字体名或 font-family 栈 */
    fontFamily?: string;
    /** 主标题字号 */
    fontSize?: number;
    /** 主标题字重 */
    fontWeight?: number;
    /** 副标题字号 */
    subFontSize?: number;
    /** 副标题字重 */
    subFontWeight?: number;
    /** 主标题字间距 */
    letterSpacing?: string;
    /** 副标题字间距 */
    subLetterSpacing?: string;
    /** 文字阴影（CSS text-shadow 值），默认 fadeIn 使用黑色发光阴影 */
    textShadow?: string;
    /** fadeIn 保留时长（帧数），过后自动淡出 */
    holdDuration?: number;
    [key: string]: any;
  };
}

interface GlitchLayerProps {
  text: string;
  offset: number;
  color: string;
  opacity: number;
  frame: number;
  glitchFrame: number;
  fontFamily: string;
  fontSize: number;
  fontWeight: number;
}

const GlitchLayer: React.FC<GlitchLayerProps> = ({
  text,
  offset,
  color,
  opacity: opacityLocal,
  frame,
  glitchFrame,
  fontFamily,
  fontSize,
  fontWeight,
}) => {
  const localFrame = frame - glitchFrame;
  const glitchIntensity = interpolate(localFrame, [0, 8, 20], [1, 1, 0]);
  const randomOffset = Math.sin(localFrame * 7.3 + offset) * 12 * glitchIntensity;
  const randomSkew = Math.sin(localFrame * 5.1 + offset * 2) * 3 * glitchIntensity;

  return (
    <div
      style={{
        position: "absolute",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontSize,
        fontWeight,
        color,
        opacity: opacityLocal * glitchIntensity,
        fontFamily,
        textTransform: "uppercase",
        textAlign: "center",
        transform: `translateX(${randomOffset}px) skewX(${randomSkew}deg)`,
        clipPath: `inset(${Math.sin(localFrame * 3.7 + offset * 3) * 30 + 30}% 0 ${Math.sin(localFrame * 4.1 + offset) * 30 + 30}% 0)`,
      }}
    >
      {text}
    </div>
  );
};

const SmashText: React.FC<SmashTextProps> = ({ mainText, subText, style }) => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();

  const entranceDuration = 40;

  // ── 从 style 读取字体配置（预设→style→硬编码兜底） ──
  const activeFontFamily = style.fontFamily || defaultFontFamily;
  const activeFontSize = style.fontSize || 130;
  const activeFontWeight = style.fontWeight || 900;
  const activeSubFontSize = style.subFontSize || 48;
  const activeSubFontWeight = style.subFontWeight || 600;
  const activeLetterSpacing = style.letterSpacing || "4px";
  const activeSubLetterSpacing = style.subLetterSpacing || "2px";

  switch (style.entrance) {
    case "drop":
      // ── 砸入 + 微震动 ──
      const dropProgress = spring({
        frame,
        fps,
        config: {
          damping: 10,
          mass: 0.6,
          stiffness: 120,
        },
        durationInFrames: entranceDuration,
      });

      const y = interpolate(dropProgress, [0, 1], [-300, 0]);
      const dropOpacity = interpolate(dropProgress, [0, 0.15, 1], [0, 1, 1]);
      const scale = interpolate(dropProgress, [0, 0.7, 1], [1.5, 1.1, 1]);

      // 落地后微震动（衰减振荡）
      const afterDrop = Math.max(0, frame - entranceDuration);
      const shakeDecay = Math.exp(-afterDrop * 0.08);
      const shakeX = Math.sin(afterDrop * 1.2) * 6 * shakeDecay;

      return (
        <AbsoluteFill
          style={{
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <div
            style={{
              transform: `translateY(${y}px) translateX(${shakeX}px) scale(${scale})`,
              opacity: dropOpacity,
              textAlign: "center",
              width: "90%",
            }}
          >
            <div
              style={{
                fontSize: activeFontSize,
                fontWeight: activeFontWeight,
                color: style.theme,
                WebkitTextStroke: style.textStroke || "16px black",
                fontFamily: activeFontFamily,
                textTransform: "uppercase",
                lineHeight: 1.2,
                letterSpacing: activeLetterSpacing,
              }}
            >
              {style.highlight && style.highlight.length > 0
                ? highlightText(mainText, style.highlight, style.highlightColor || "#FF0000")
                : mainText}
            </div>
            {subText && (
              <div
                style={{
                  fontSize: activeSubFontSize,
                  fontWeight: activeSubFontWeight,
                  color: style.theme,
                  fontFamily: activeFontFamily,
                  marginTop: 20,
                  letterSpacing: activeSubLetterSpacing,
                }}
              >
                {subText}
              </div>
            )}
          </div>
        </AbsoluteFill>
      );

    case "glitch":
      // ── 故障抖动 ──
      const glitchEnter = spring({
        frame,
        fps,
        config: { damping: 15, mass: 0.8, stiffness: 80 },
        durationInFrames: entranceDuration,
      });

      const gScale = interpolate(glitchEnter, [0, 1], [0.3, 1]);
      const gOpacity = interpolate(glitchEnter, [0, 0.2, 1], [0, 1, 1]);
      const glitchStartFrame = Math.min(
        entranceDuration - 10,
        Math.max(entranceDuration - 20, 10),
      );

      // 是否在活跃故障窗口中
      const inGlitch =
        frame >= glitchStartFrame &&
        frame < glitchStartFrame + 20;

      return (
        <AbsoluteFill
          style={{
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <div
            style={{
              transform: `scale(${gScale})`,
              opacity: gOpacity,
              width: "100%",
              height: "100%",
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            {/* 干净全文 — 故障前后始终可见，故障期间隐藏 */}
            <div
              style={{
                fontSize: activeFontSize,
                fontWeight: activeFontWeight,
                color: style.theme,
                fontFamily: activeFontFamily,
                textTransform: "uppercase",
                textAlign: "center",
                lineHeight: 1.2,
                letterSpacing: activeLetterSpacing,
                visibility: inGlitch ? "hidden" : "visible",
              }}
            >
              {style.highlight && style.highlight.length > 0
                ? highlightText(
                    mainText,
                    style.highlight,
                    style.highlightColor || "#FF0000",
                  )
                : mainText}
            </div>

            {/* 故障层 — 仅活跃故障窗口显示 */}
            {inGlitch && (
              <>
                <GlitchLayer
                  text={mainText}
                  offset={0}
                  color="#FF0044"
                  opacity={0.8}
                  frame={frame}
                  glitchFrame={glitchStartFrame}
                  fontFamily={activeFontFamily}
                  fontSize={activeFontSize}
                  fontWeight={activeFontWeight}
                />
                <GlitchLayer
                  text={mainText}
                  offset={3}
                  color="#00FFF0"
                  opacity={0.8}
                  frame={frame}
                  glitchFrame={glitchStartFrame}
                  fontFamily={activeFontFamily}
                  fontSize={activeFontSize}
                  fontWeight={activeFontWeight}
                />
                <GlitchLayer
                  text={mainText}
                  offset={6}
                  color={style.theme}
                  opacity={1}
                  frame={frame}
                  glitchFrame={glitchStartFrame}
                  fontFamily={activeFontFamily}
                  fontSize={activeFontSize}
                  fontWeight={activeFontWeight}
                />
              </>
            )}

            {subText && (
              <div
                style={{
                  fontSize: activeSubFontSize,
                  fontWeight: activeSubFontWeight,
                  color: style.theme,
                  fontFamily: activeFontFamily,
                  marginTop: 80,
                }}
              >
                {subText}
              </div>
            )}
          </div>
        </AbsoluteFill>
      );

    case "splitReveal":
      // ── 文字裂开 → 新文字弹出 ──
      const splitProgress = spring({
        frame,
        fps,
        config: { damping: 8, mass: 0.7, stiffness: 100 },
        durationInFrames: entranceDuration,
      });

      const halfLen = Math.ceil(mainText.length / 2);
      const leftHalf = mainText.slice(0, halfLen);
      const rightHalf = mainText.slice(halfLen);

      const slideOut = interpolate(splitProgress, [0, 0.5, 1], [0, -60, 0]);
      const rightSlide = interpolate(splitProgress, [0, 0.5, 1], [0, 60, 0]);
      const sOpacity = interpolate(splitProgress, [0, 0.3, 0.7, 1], [0, 1, 0.8, 1]);
      const sScale = interpolate(splitProgress, [0, 0.5, 1], [1.5, 0.8, 1]);

      const leftColor = interpolate(splitProgress, [0, 0.5, 1], [1, 0.3, 1]);
      void(leftColor);
      const rightColor = interpolate(splitProgress, [0, 0.5, 1], [1, 0.3, 1]);

      return (
        <AbsoluteFill
          style={{
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <div
            style={{
              transform: `scale(${sScale})`,
              opacity: sOpacity,
              textAlign: "center",
              width: "100%",
            }}
          >
            {/* 上半：裂开的文字 */}
            <div
              style={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                fontSize: activeFontSize,
                fontWeight: activeFontWeight,
                fontFamily: activeFontFamily,
                textTransform: "uppercase",
                lineHeight: 1.2,
                letterSpacing: activeLetterSpacing,
              }}
            >
              <span
                style={{
                  color: style.theme,
                  WebkitTextStroke: style.textStroke || "16px black",
                  transform: `translateX(${slideOut}px)`,
                  display: "inline-block",
                }}
              >
                {leftHalf}
              </span>
              {/* 裂开缝隙中的发光 */}
              <span
                style={{
                  width: 20,
                  height: 100,
                  background: `linear-gradient(to bottom, transparent, ${style.theme}, transparent)`,
                  opacity: interpolate(splitProgress, [0.3, 0.5, 0.7], [0, 1, 0]),
                  margin: "0 10px",
                }}
              />
              <span
                style={{
                  color: `rgba(${20 * rightColor}, ${250 * rightColor}, ${255 * rightColor}, ${rightColor})`,
                  WebkitTextStroke: style.textStroke || "16px black",
                  transform: `translateX(${rightSlide}px)`,
                  display: "inline-block",
                }}
              >
                {rightHalf}
              </span>
            </div>

            {subText && (
              <div
                style={{
                  fontSize: activeSubFontSize,
                  fontWeight: activeSubFontWeight,
                  color: style.theme,
                  fontFamily: activeFontFamily,
                  marginTop: 30,
                  letterSpacing: activeSubLetterSpacing,
                  opacity: interpolate(splitProgress, [0, 0.6, 1], [0, 0.5, 1]),
                }}
              >
                {subText}
              </div>
            )}
          </div>
        </AbsoluteFill>
      );

    case "fadeIn":
      // ── 淡入出现：opacity 0→1 + 轻微上移 30px→0 + 文字阴影 ──
      const fadeProgress = spring({
        frame,
        fps,
        config: { damping: 18, mass: 0.5, stiffness: 60 },
        durationInFrames: entranceDuration,
      });
      const fadeInOpacity = interpolate(fadeProgress, [0, 1], [0, 1]);
      const fY = interpolate(fadeProgress, [0, 1], [30, 0]);

      // ── 自动淡出：holdDuration（帧数）后开始淡出 ──
      const holdFrames2 = style.holdDuration ?? 0;
      const fadeOutStart = entranceDuration + holdFrames2;
      const finalOpacity: number =
        frame > fadeOutStart && holdFrames2 > 0
          ? fadeInOpacity *
            (1 -
              Math.min((frame - fadeOutStart) / 20, 1))
          : fadeInOpacity;

      return (
        <AbsoluteFill
          style={{
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <div
            style={{
              transform: `translateY(${fY}px)`,
              opacity: finalOpacity,
              textAlign: "center",
              width: "90%",
            }}
          >
            <div
              style={{
                fontSize: activeFontSize,
                fontWeight: activeFontWeight,
                color: style.theme,
                WebkitTextStroke: style.textStroke || "16px black",
                textShadow:
                  style.textShadow ||
                  "0 0 40px rgba(0,0,0,0.5), 0 0 20px rgba(0,0,0,0.3)",
                fontFamily: activeFontFamily,
                textTransform: "uppercase",
                lineHeight: 1.2,
                letterSpacing: activeLetterSpacing,
              }}
            >
              {style.highlight && style.highlight.length > 0
                ? highlightText(mainText, style.highlight, style.highlightColor || "#00F5FF")
                : mainText}
            </div>
            {subText && (
              <div
                style={{
                  fontSize: activeSubFontSize,
                  fontWeight: activeSubFontWeight,
                  color: style.theme,
                  fontFamily: activeFontFamily,
                  marginTop: 20,
                  letterSpacing: activeSubLetterSpacing,
                  textShadow:
                    style.textShadow ||
                    "0 0 30px rgba(0,0,0,0.4)",
                }}
              >
                {subText}
              </div>
            )}
          </div>
        </AbsoluteFill>
      );

    default:
      return null;
  }
};

/** 高亮文本中的特定词 */
function highlightText(
  text: string,
  highlights: string[],
  highlightColor: string,
): React.ReactNode {
  const parts: React.ReactNode[] = [];
  let remaining = text;
  highlights.forEach((word, idx) => {
    const pos = remaining.indexOf(word);
    if (pos === -1) {
      parts.push(remaining);
      remaining = "";
      return;
    }
    if (pos > 0) parts.push(remaining.slice(0, pos));
    parts.push(
      <span
        key={idx}
        style={{
          color: highlightColor,
          textShadow: `0 0 30px ${highlightColor}80`,
        }}
      >
        {word}
      </span>,
    );
    remaining = remaining.slice(pos + word.length);
  });
  if (remaining) parts.push(remaining);
  return <>{parts}</>;
}

export default SmashText;
;
    var _remotion_globalVariableA;
    var _remotion_globalVariableB;
    // Legacy CSS implementations will `eval` browser code in a Node.js context
    // to extract CSS. For backwards compatibility, we need to check we're in a
    // browser context before continuing.
    if (typeof self !== 'undefined' &&
        // AMP / No-JS mode does not inject these helpers:
        '$RefreshHelpers$' in self) {
        const currentExports = __webpack_module__.exports;
        const prevExports = (_remotion_globalVariableB = (_remotion_globalVariableA = __webpack_module__.hot.data) === null || _remotion_globalVariableA === void 0 ? void 0 : _remotion_globalVariableA.prevExports) !== null && _remotion_globalVariableB !== void 0 ? _remotion_globalVariableB : null;
        // This cannot happen in MainTemplate because the exports mismatch between
        // templating and execution.
        self.$RefreshHelpers$.registerExportsForReactRefresh(currentExports, __webpack_module__.id);
        // A module can be accepted automatically based on its exports, e.g. when
        // it is a Refresh Boundary.
        if (self.$RefreshHelpers$.isReactRefreshBoundary(currentExports)) {
            // Save the previous exports on update so we can compare the boundary
            // signatures.
            __webpack_module__.hot.dispose((data) => {
                data.prevExports = currentExports;
            });
            // Unconditionally accept an update to this module, we'll check if it's
            // still a Refresh Boundary later.
            __webpack_module__.hot.accept();
            // This field is set when the previous version of this module was a
            // Refresh Boundary, letting us know we need to check for invalidation or
            // enqueue an update.
            if (prevExports !== null) {
                // A boundary can become ineligible if its exports are incompatible
                // with the previous exports.
                //
                // For example, if you add/remove/change exports, we'll want to
                // re-execute the importing modules, and force those components to
                // re-render. Similarly, if you convert a class component to a
                // function, we want to invalidate the boundary.
                if (self.$RefreshHelpers$.shouldInvalidateReactRefreshBoundary(prevExports, currentExports)) {
                    __webpack_module__.hot.invalidate();
                }
                else {
                    self.$RefreshHelpers$.scheduleUpdate();
                }
            }
        }
        else {
            // Since we just executed the code for the module, it's possible that the
            // new exports made it ineligible for being a boundary.
            // We only care about the case when we were _previously_ a boundary,
            // because we already accepted this update (accidental side effect).
            const isNoLongerABoundary = prevExports !== null;
            if (isNoLongerABoundary) {
                __webpack_module__.hot.invalidate();
            }
        }
    }
